"use client";

import {
  ActionIcon,
  Box,
  Button,
  Fieldset,
  Group,
  Portal,
  Stack,
  Text,
  TextInput,
  Transition,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMounted } from "@mantine/hooks";
import { IconX } from "@tabler/icons-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { showErrorNotification } from "@/helpers/notifications";
import { createClient } from "@/lib/supabase";
import {
  DEV_USER_FREE,
  DEV_USER_PREMIUM,
  DEV_USER_STANDARD,
} from "@/test-data";

export const DevUserLoginButton = () => {
  const t = useTranslations("app.login.DevUserLoginButton");
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(true);
  const mounted = useMounted();

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) =>
        /^\S+@\S+\.\S+$/.test(value) ? null : t("errors.invalidEmail"),
      password: (value) =>
        value.length > 0 ? null : t("errors.passwordRequired"),
    },
  });

  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setIsLoading(false);

    if (error) {
      showErrorNotification({
        title: t("errors.loginFailed"),
        message: error.message,
      });
      return;
    }

    const nextParam = searchParams.get("next");
    router.push(nextParam || "/dashboard");
  };

  return (
    <Portal target="#dev-login-portal">
      <Transition
        mounted={mounted && visible}
        transition="slide-right"
        duration={300}
      >
        {(styles) => (
          <Box
            style={{
              ...styles,
              borderRadius: "var(--mantine-radius-md)",
              background:
                "repeating-linear-gradient(45deg, var(--mantine-color-yellow-5), var(--mantine-color-yellow-5) 3px, var(--mantine-color-dark-7) 3px, var(--mantine-color-dark-7) 6px)",
            }}
            p={5}
          >
            <Box
              bg="dark.7"
              p="md"
              style={{ borderRadius: "var(--mantine-radius-sm)" }}
            >
              <Box pos="relative" mb="xs">
                <Text size="sm" fw={700} c="yellow" tt="uppercase" ta="center">
                  {t("title")}
                </Text>
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => setVisible(false)}
                  pos="absolute"
                  top={0}
                  right={0}
                >
                  <IconX />
                </ActionIcon>
              </Box>

              <Group grow gap="xs">
                <Button
                  color="yellow.5"
                  size="xs"
                  loading={isLoading}
                  onClick={() =>
                    handleLogin(
                      DEV_USER_PREMIUM.email,
                      DEV_USER_PREMIUM.password,
                    )
                  }
                >
                  {t("premiumButton")}
                </Button>

                <Button
                  color="blue.5"
                  size="xs"
                  loading={isLoading}
                  onClick={() =>
                    handleLogin(
                      DEV_USER_STANDARD.email,
                      DEV_USER_STANDARD.password,
                    )
                  }
                >
                  {t("standardButton")}
                </Button>

                <Button
                  color="dark.2"
                  size="xs"
                  loading={isLoading}
                  onClick={() =>
                    handleLogin(DEV_USER_FREE.email, DEV_USER_FREE.password)
                  }
                >
                  {t("freeButton")}
                </Button>
              </Group>

              <form
                onSubmit={form.onSubmit((values) =>
                  handleLogin(values.email, values.password),
                )}
              >
                <Fieldset legend={t("customLegend")} radius="sm" mt="xs">
                  <Stack gap="xs">
                    <TextInput
                      placeholder={t("emailPlaceholder")}
                      type="email"
                      size="xs"
                      {...form.getInputProps("email")}
                    />
                    <TextInput
                      placeholder={t("passwordPlaceholder")}
                      type="password"
                      size="xs"
                      {...form.getInputProps("password")}
                    />
                    <Button
                      type="submit"
                      variant="light"
                      size="xs"
                      loading={isLoading}
                    >
                      {t("loginButton")}
                    </Button>
                  </Stack>
                </Fieldset>
              </form>
            </Box>
          </Box>
        )}
      </Transition>
    </Portal>
  );
};
